import { create } from 'zustand';

import { createJournalEntry, getJournalEntriesByHunt } from '@/services/hunt-journal-service';
import type { HuntJournalEntry, CreateJournalEntryInput } from '@/types/hunt-journal-entry';

interface HuntJournalStore {
  /** Entries keyed by huntId — loaded on demand per hunt detail page visit. */
  entriesByHuntId: Record<string, HuntJournalEntry[]>;
  isLoading: boolean;
  error: string | null;

  /** Load (or refresh) all active entries for a hunt. */
  loadEntries: (huntId: string) => Promise<void>;

  /** Create a new entry and refresh the list for the hunt. */
  addEntry: (input: Omit<CreateJournalEntryInput, 'userId'>) => Promise<HuntJournalEntry>;
}

export const useHuntJournalStore = create<HuntJournalStore>((set, get) => ({
  entriesByHuntId: {},
  isLoading: false,
  error: null,

  loadEntries: async (huntId) => {
    set({ isLoading: true, error: null });
    try {
      const entries = await getJournalEntriesByHunt(huntId);
      set((state) => ({
        entriesByHuntId: { ...state.entriesByHuntId, [huntId]: entries },
        isLoading: false,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load journal entries';
      console.error('[HuntJournalStore] loadEntries:', err);
      set({ error: message, isLoading: false });
    }
  },

  addEntry: async (input) => {
    try {
      const entry = await createJournalEntry(input);
      // Refresh the list after adding
      await get().loadEntries(input.huntId);
      return entry;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add journal entry';
      console.error('[HuntJournalStore] addEntry:', err);
      set({ error: message });
      throw err;
    }
  },
}));

/**
 * Stable empty array reused across renders for hunts with no entries.
 *
 * Without this, a selector like `s.entriesByHuntId[huntId] ?? []` returns a
 * brand-new `[]` on every invocation — Zustand treats that as a state change
 * (reference inequality), schedules a re-render, the selector runs again,
 * returns yet another `[]`, and the component loops until React bails with
 * "Maximum update depth exceeded" (React error #185).
 *
 * Matches the pattern in `share-store.ts` (`selectSharesForProperty`).
 */
const EMPTY_ENTRIES: readonly HuntJournalEntry[] = Object.freeze([]);

/**
 * Selector helper: entries for a given hunt, or a stable empty array.
 *
 * Must be called OUTSIDE the Zustand selector (i.e., `useHuntJournalStore(
 * (s) => s.entriesByHuntId)` then `selectEntriesForHunt(map, huntId)`) so the
 * selector returns a stable map reference and this helper only materializes
 * the per-hunt array with a stable empty fallback.
 */
export function selectEntriesForHunt(
  entriesByHuntId: Record<string, HuntJournalEntry[]>,
  huntId: string,
): readonly HuntJournalEntry[] {
  return entriesByHuntId[huntId] ?? EMPTY_ENTRIES;
}
